/**
 * Platform Tabs Layout
 * Reusable component for displaying platform-specific content in tabs
 * Handles 1 or 2 platforms (APP/WEB) automatically
 *
 * When tabs change, navigates to the new platform URL to trigger data reload.
 */

import React from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import {Box, Tabs, Tab, Paper} from '@mui/material';

interface PlatformTabsLayoutProps {
  platforms: ('app' | 'web')[];
  renderTabContent: (platform: 'app' | 'web') => React.ReactNode;
  trialId: string;
  version: string;
  sessionId?: string;
  /** Wizard URL segment: req | plan | execute | validate */
  wizardStepSegment?: string;
}

export const PlatformTabsLayout: React.FC<PlatformTabsLayoutProps> = ({
  platforms,
  renderTabContent,
  trialId,
  version,
  sessionId: propSessionId,
  wizardStepSegment = 'req',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {platform: urlPlatform} = useParams<{platform: 'app' | 'web'}>();
  const locationState = location.state as { platforms?: ('app' | 'web')[]; sessionId?: string } | null;
  // Prefer prop sessionId, then URL sessionId, then state sessionId
  const sessionId = propSessionId || locationState?.sessionId;

  // Determine active tab from URL platform
  const activeTab = platforms.indexOf(urlPlatform as 'app' | 'web');
  const currentPlatform = urlPlatform as 'app' | 'web';

  // Handle tab change - navigate to new platform URL (preserve parent-provided state)
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newPlatform = platforms[newValue];
    if (newPlatform !== urlPlatform) {
      // Include sessionId in URL path for bookmarking/direct access
      const sessionPath = sessionId ? `/${encodeURIComponent(sessionId)}` : '';
      navigate(`/wizard/${trialId}/${version}/${newPlatform}${sessionPath}/${wizardStepSegment}`, {
        state: {
          platforms,
          sessionId,
        },
      });
    }
  };

  // If only one platform, no tabs needed - just show content
  if (platforms.length === 1) {
    return (
      <Box sx={{mt: 1}}>
        {renderTabContent(platforms[0])}
      </Box>
    );
  }

  return (
    <Box sx={{mt: 2}}>
      {/* Platform Tabs */}
      <Paper
        elevation={6}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: 2, borderColor: 'divider',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        <Tabs
          value={activeTab >= 0 ? activeTab : 0}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{
            bgcolor: 'action.selected',
            minHeight: 56,
          }}
        >
          {platforms.map((platform) => (
            <Tab
              key={platform}
              label={platform.toUpperCase()}
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                py: 2,
                transition: 'all 0.3s ease',
                // Unselected state - greyed out
                color: 'text.secondary',
                bgcolor: 'action.disabledBackground',
                opacity: 0.7,
                '&.Mui-selected': {
                  bgcolor: 'background.paper',
                  color: 'primary.main',
                  fontWeight: 700,
                  opacity: 1,
                  boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                },
                '&:hover:not(.Mui-selected)': {
                  bgcolor: 'action.selected',
                  opacity: 0.85,
                },
              }}
            />
          ))}
        </Tabs>

        {/* Tab Content - visually connected to tabs */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'background.paper',
            minHeight: 400,
            borderTop: 3, borderColor: 'primary.main',
          }}
        >
          {renderTabContent(currentPlatform)}
        </Box>
      </Paper>
    </Box>
  );
};
